import { useEffect, useMemo, useRef, useState } from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    ChipProps,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import {
    ColumnsPanelTrigger,
    DataGridPro,
    GridActionsCellItem,
    GridApiPro,
    GridColDef,
    GridRenderCellParams,
    GridRowParams,
    GridViewColumnIcon,
    QuickFilter,
    QuickFilterControl,
    Toolbar,
    ToolbarButton,
    gridPaginationModelSelector,
    useGridApiContext,
    useGridApiRef,
} from '@mui/x-data-grid-pro'
import RefreshIcon from '@mui/icons-material/Refresh'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import dayjs from 'dayjs'
import humanizeDuration from 'humanize-duration'
import { enqueueSnackbar } from 'notistack'
import { Link } from 'wouter'
import API from '@enums/api'
import useDataSource from '@hooks/useDataSource'
import { OptionType } from '@components/MultiSelectAutocomplete'
import { PodiumFleetRow, PodiumStatus } from '@types'
import PodiumActionDialog, { PodiumAction } from './PodiumActionDialog'

/** Chip colour per operational status. */
const STATUS_COLOR: Record<PodiumStatus, ChipProps['color']> = {
    recording: 'error',
    paused: 'warning',
    standby: 'success',
    initializing: 'info',
    offline: 'default',
    disabled: 'secondary',
}

const STATUS_OPTIONS: PodiumStatus[] = [
    'initializing',
    'standby',
    'recording',
    'paused',
    'offline',
    'disabled',
]

type OnlineFilter = 'all' | 'online' | 'offline'

/** Toolbar with a substring search (name / hardware ID) and a live-refresh button. */
function PodiumsToolbar() {
    const apiRef = useGridApiContext() as React.MutableRefObject<GridApiPro>

    // Re-fetch the current page/sort/filter. Cache is disabled on the grid, so this always
    // hits the server and picks up fresh statuses. The grid shows its own loading overlay.
    const handleRefresh = () => apiRef.current.dataSource.fetchRows()

    return (
        <Toolbar>
            <QuickFilter
                style={{ marginRight: 'auto' }}
                // Substring match server-side, so keep the whole input as one value.
                parser={(input) => (input.trim() ? [input.trim()] : [])}
                formatter={(values) => values[0] ?? ''}
                debounceMs={300}
            >
                <QuickFilterControl
                    render={({ ref, ...controlProps }) => (
                        <TextField
                            inputRef={ref}
                            label="Search name / hardware ID"
                            variant="standard"
                            size="small"
                            sx={{ minWidth: 240 }}
                            {...controlProps}
                        />
                    )}
                />
            </QuickFilter>
            <Tooltip title="Columns">
                <ColumnsPanelTrigger render={<ToolbarButton />}>
                    <GridViewColumnIcon fontSize="small" />
                </ColumnsPanelTrigger>
            </Tooltip>
            <ToolbarButton
                render={
                    <Button
                        variant="text"
                        onClick={handleRefresh}
                        startIcon={<RefreshIcon />}
                        sx={{ padding: '4px 8px' }}
                    >
                        Refresh
                    </Button>
                }
            />
        </Toolbar>
    )
}

export default function PodiumsDataGrid() {
    const apiRef = useGridApiRef()

    const [statusFilter, setStatusFilter] = useState<PodiumStatus[]>([])
    const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('all')
    const [orgFilter, setOrgFilter] = useState<OptionType | null>(null)
    const [orgOptions, setOrgOptions] = useState<OptionType[]>([])
    const [pendingAction, setPendingAction] = useState<{
        row: PodiumFleetRow
        type: PodiumAction
    } | null>(null)
    // Set when a fleet fetch fails, so we can show an explicit error panel instead of an
    // empty table (which would read as "no podiums"). Cleared on the next successful load.
    const [loadError, setLoadError] = useState<string | null>(null)

    // Latest explicit filters, read by the (memoised) getRows closure at call time. Mutated in
    // render so any fetch triggered after this render — pagination, sort, search, refresh — uses
    // the current values.
    const filtersRef = useRef<{
        status: PodiumStatus[]
        isOnline?: boolean
        orgId?: string
    }>({ status: [] })
    filtersRef.current = {
        status: statusFilter,
        isOnline:
            onlineFilter === 'all' ? undefined : onlineFilter === 'online',
        orgId: orgFilter?.id != null ? String(orgFilter.id) : undefined,
    }

    const { dataSource } = useDataSource({
        getRows: async ({ paginationModel, sortModel, filterModel }) => {
            const failToLoad = (reason: string) => {
                const message = `Couldn't load podiums (${reason}).`
                setLoadError(message)
                enqueueSnackbar({ variant: 'error', message })
                // Return an empty page rather than throwing: the error panel is our signal,
                // and this keeps the grid mounted so Retry can re-fetch.
                return { rows: [], rowCount: 0 }
            }

            const res = await API.ha.podium
                .getFleet({
                    paginationModel,
                    sortModel,
                    filterModel,
                    orgId: filtersRef.current.orgId,
                    status: filtersRef.current.status,
                    isOnline: filtersRef.current.isOnline,
                })
                .catch(() => null)

            if (!res) return failToLoad("the server couldn't be reached")
            if (!res.ok) {
                return failToLoad(
                    typeof res.status === 'number'
                        ? `HTTP ${res.status}`
                        : 'the request failed'
                )
            }
            if (!res.value?.pagination) {
                return failToLoad('the response was not in the expected format')
            }

            setLoadError(null)
            return {
                rows: res.value.data,
                rowCount: res.value.pagination.total,
            }
        },
    })

    // Load organisations for the org filter once.
    useEffect(() => {
        let active = true
        API.searches.listAllOrgs().then((res) => {
            if (active && res.ok) setOrgOptions(res.value)
        })
        return () => {
            active = false
        }
    }, [])

    // The status/online/org filters live outside the grid's own model, so changing one won't
    // trigger the data source by itself — we refetch manually here. We skip the initial mount
    // because the grid fires its own first fetch then; refetching here too would duplicate it.
    const didMount = useRef(false)
    useEffect(() => {
        if (!didMount.current) {
            didMount.current = true
            return
        }
        const api = apiRef.current
        if (!api) return
        // Jumping back to page 1 triggers a fetch on its own; if we're already there, fetch directly.
        if (gridPaginationModelSelector(apiRef).page !== 0) {
            api.setPage(0)
        } else {
            api.dataSource.fetchRows()
        }
    }, [statusFilter, onlineFilter, orgFilter, apiRef])

    const closeDialog = () => setPendingAction(null)
    const handleConfirmed = () => {
        setPendingAction(null)
        apiRef.current?.dataSource.fetchRows()
    }

    const columns = useMemo<GridColDef<PodiumFleetRow>[]>(() => {
        // Column filters are server-ignored (only the quick-search maps to a server filter), so
        // no column exposes a filter menu; `sortable` is opted back in per whitelisted column.
        const cols: GridColDef<PodiumFleetRow>[] = [
            {
                field: 'name',
                headerName: 'Podium',
                flex: 1.4,
                type: 'string',
                renderCell: (params: GridRenderCellParams<PodiumFleetRow>) => (
                    <Stack
                        direction="column"
                        justifyContent="center"
                        height="100%"
                    >
                        {params.row.hardware_id ? (
                            // The detail page is keyed by hardware id, so a row without
                            // one (shouldn't happen, but the column is nullable) stays
                            // plain text rather than linking nowhere.
                            <Link
                                href={`/podiums/${params.row.hardware_id}`}
                                style={{ lineHeight: 1.43 }}
                            >
                                {params.row.name || 'Unnamed'}
                            </Link>
                        ) : (
                            <Typography variant="body2">
                                {params.row.name || 'Unnamed'}
                            </Typography>
                        )}
                        <Typography variant="overline" color="gray">
                            {params.row.hardware_id ?? '—'}
                        </Typography>
                    </Stack>
                ),
            },
            {
                field: 'org_name',
                headerName: 'Organization',
                flex: 1,
                type: 'string',
                // Not a whitelisted server sort column.
                sortable: false,
                renderCell: (params: GridRenderCellParams<PodiumFleetRow>) => {
                    if (!params.row.org_id) {
                        return (
                            <Typography variant="body2" color="gray">
                                Unclaimed
                            </Typography>
                        )
                    }
                    return (
                        <Link href={`/organizations/${params.row.org_id}`}>
                            {params.row.org_name ?? params.row.org_id}
                        </Link>
                    )
                },
            },
            {
                field: 'status',
                headerName: 'Status',
                flex: 0.8,
                type: 'string',
                renderCell: (params: GridRenderCellParams<PodiumFleetRow>) => (
                    <Chip
                        label={params.row.status}
                        color={STATUS_COLOR[params.row.status] ?? 'default'}
                        size="small"
                        variant="outlined"
                    />
                ),
            },
            {
                field: 'is_online',
                headerName: 'Connection',
                flex: 0.7,
                type: 'boolean',
                renderCell: (params: GridRenderCellParams<PodiumFleetRow>) => (
                    <Chip
                        label={params.row.is_online ? 'Online' : 'Offline'}
                        color={params.row.is_online ? 'success' : 'default'}
                        size="small"
                    />
                ),
            },
            {
                field: 'app_version',
                headerName: 'Version',
                flex: 0.7,
                type: 'string',
                valueFormatter: (value) => value || '—',
            },
            {
                field: 'disk_free_percent',
                headerName: 'Disk Free',
                flex: 0.6,
                type: 'number',
                valueFormatter: (value) =>
                    typeof value === 'number' ? `${value}%` : '—',
            },
            {
                field: 'failed_job_count',
                headerName: 'Failed Jobs',
                flex: 0.6,
                type: 'number',
                valueFormatter: (value) =>
                    typeof value === 'number' ? value : '—',
            },
            {
                field: 'uptime_seconds',
                headerName: 'Uptime',
                flex: 0.8,
                type: 'number',
                valueFormatter: (value) =>
                    typeof value === 'number'
                        ? humanizeDuration(value * 1000, {
                              largest: 2,
                              round: true,
                              units: ['d', 'h', 'm'],
                          })
                        : '—',
            },
            {
                field: 'last_health_at',
                headerName: 'Last Health',
                flex: 1,
                type: 'dateTime',
                valueGetter: (value) =>
                    value ? new Date(value as string) : null,
                valueFormatter: (value) =>
                    value
                        ? dayjs(value as Date).format('YYYY/MM/DD HH:mm')
                        : '—',
            },
            {
                field: 'created_at',
                headerName: 'Added',
                flex: 1,
                type: 'dateTime',
                valueGetter: (value) =>
                    value ? new Date(value as string) : null,
                valueFormatter: (value) =>
                    value
                        ? dayjs(value as Date).format('YYYY/MM/DD HH:mm')
                        : '—',
            },
            {
                field: 'actions',
                type: 'actions',
                headerName: 'Actions',
                width: 90,
                getActions: (params: GridRowParams<PodiumFleetRow>) => {
                    const row = params.row
                    // Every command is keyed by hardware_id and pointless once a device is
                    // (being) decommissioned.
                    const disabled =
                        !row.hardware_id ||
                        !!row.decommissioned_at ||
                        !!row.decommission_requested_at
                    const isDisabled =
                        row.status === 'disabled' ||
                        row.desired_admin_state === 'disabled'

                    const toggle = isDisabled ? (
                        <GridActionsCellItem
                            key="enable"
                            icon={<CheckCircleIcon />}
                            label="Enable"
                            disabled={disabled}
                            onClick={() =>
                                setPendingAction({ row, type: 'enable' })
                            }
                        />
                    ) : (
                        <GridActionsCellItem
                            key="disable"
                            icon={<BlockIcon />}
                            label="Disable"
                            disabled={disabled}
                            onClick={() =>
                                setPendingAction({ row, type: 'disable' })
                            }
                        />
                    )

                    return [
                        toggle,
                        <GridActionsCellItem
                            key="wipe"
                            icon={<DeleteForeverIcon />}
                            label="Wipe"
                            disabled={disabled}
                            onClick={() =>
                                setPendingAction({ row, type: 'wipe' })
                            }
                            showInMenu
                        />,
                    ]
                },
            },
        ]

        return cols.map((col) => ({ filterable: false, ...col }))
    }, [])

    const hasFilters =
        statusFilter.length > 0 || onlineFilter !== 'all' || orgFilter !== null

    return (
        <Box width="100%">
            <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
                sx={{ mb: 2 }}
            >
                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel id="podium-status-label">Status</InputLabel>
                    <Select
                        labelId="podium-status-label"
                        multiple
                        value={statusFilter}
                        input={<OutlinedInput label="Status" />}
                        renderValue={(selected) =>
                            (selected as PodiumStatus[]).join(', ')
                        }
                        onChange={(e) => {
                            // MUI surfaces the value as a string on native-autofill.
                            const v = e.target.value as PodiumStatus[] | string
                            setStatusFilter(
                                typeof v === 'string'
                                    ? (v.split(',') as PodiumStatus[])
                                    : v
                            )
                        }}
                    >
                        {STATUS_OPTIONS.map((s) => (
                            <MenuItem key={s} value={s}>
                                <Checkbox checked={statusFilter.includes(s)} />
                                <ListItemText primary={s} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="podium-online-label">Connection</InputLabel>
                    <Select
                        labelId="podium-online-label"
                        label="Connection"
                        value={onlineFilter}
                        onChange={(e) =>
                            setOnlineFilter(e.target.value as OnlineFilter)
                        }
                    >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="online">Online</MenuItem>
                        <MenuItem value="offline">Offline</MenuItem>
                    </Select>
                </FormControl>

                <Autocomplete
                    size="small"
                    sx={{ minWidth: 240 }}
                    options={orgOptions}
                    value={orgFilter}
                    onChange={(_e, v) => setOrgFilter(v)}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    getOptionLabel={(o) => o.label}
                    renderInput={(params) => (
                        <TextField {...params} label="Organization" />
                    )}
                />

                {hasFilters && (
                    <Button
                        size="small"
                        onClick={() => {
                            setStatusFilter([])
                            setOnlineFilter('all')
                            setOrgFilter(null)
                        }}
                    >
                        Clear filters
                    </Button>
                )}
            </Stack>

            {loadError && (
                <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() =>
                                apiRef.current?.dataSource.fetchRows()
                            }
                        >
                            Retry
                        </Button>
                    }
                >
                    {loadError} The podium service may be unavailable in this
                    environment.
                </Alert>
            )}

            <Box
                sx={{
                    height: 600,
                    width: '100%',
                    display: loadError ? 'none' : 'block',
                }}
            >
                <DataGridPro<PodiumFleetRow>
                    apiRef={apiRef}
                    columns={columns}
                    pagination
                    paginationMode="server"
                    sortingMode="server"
                    filterMode="server"
                    dataSource={dataSource}
                    // Always fetch fresh so pagination and the Refresh button reflect live statuses.
                    dataSourceCache={null}
                    pageSizeOptions={[10, 25, 50, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: { pageSize: 25, page: 0 },
                        },
                        sorting: {
                            sortModel: [
                                { field: 'last_health_at', sort: 'desc' },
                            ],
                        },
                    }}
                    disableRowSelectionOnClick
                    disableVirtualization
                    showToolbar
                    slots={{ toolbar: PodiumsToolbar }}
                    getRowId={(row) => row.id}
                />
            </Box>

            <PodiumActionDialog
                action={pendingAction?.type ?? null}
                podium={pendingAction?.row ?? null}
                onClose={closeDialog}
                onConfirmed={handleConfirmed}
            />
        </Box>
    )
}
